import "./assets/main.css";
import { createApp } from "vue";
import { createAuth0 } from "@auth0/auth0-vue";
import { createGettext } from "vue3-gettext";
import { createPinia } from "pinia";
import * as Sentry from "@sentry/vue";
import App from "./App.vue";
import router from "./router";
import translations from "./language/translations.json";
import moment from "moment";
import "moment/dist/locale/fr";
import axios from "axios";
import { useAlertStore } from "@/stores/alert";
import VueBaseTooltip from 'vue-base-tooltip'

import 'vue-base-tooltip/style.css'

// Force HTTPS in production to satisfy Auth0 SPA SDK secure origin requirement.
// We must halt execution entirely to prevent the Auth0 SDK from initializing on
// an insecure origin before the browser completes the redirect.
if (window.location.hostname !== "localhost" && window.location.protocol === "http:") {
  window.location.replace(window.location.href.replace("http:", "https:"));
  throw new Error("Redirecting to HTTPS — halting script execution");
}

const app = createApp(App);

if (window.location.hostname !== "localhost") {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_FRONTEND_DSN,
    tunnel: "/api/sentry",
  });
}

const gettext = createGettext({
  availableLanguages: {
    fr: "Français",
  },
  defaultLanguage: "fr",
  translations: translations,
});

const formatDateTimeLong = (date, format = "Do MMMM YYYY, h:mm:ss a") => {
  moment.locale(gettext.language);
  return moment(date).format(format);
};

const moneyFormatter = new Intl.NumberFormat(gettext.language, {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatMoney = (value) => {
  if (typeof value !== "number") return "";
  return moneyFormatter.format(value);
};

moment.locale(gettext.defaultLanguage);

import { useAuthStore } from "@/stores/auth";

axios.interceptors.response.use(
  (response) => {
    const emailVerified = response.headers["x-email-verified"];
    if (emailVerified !== undefined) {
      const authStore = useAuthStore();
      authStore.setEmailVerified(emailVerified === "true");
    }

    return response;
  },
  (error) => {
    // On 401, let the caller handle re-authentication instead of showing alerts
    if (error?.response?.status === 401) {
      return Promise.reject(error);
    }

    const alertStore = useAlertStore();
    const errorList = error?.response?.data?.errors;

    if (Array.isArray(errorList)) {
      errorList.forEach(() => {
        let message = "Une erreur s'est produite, vérifiez ou réessayez plus tard";
        alertStore.addAlert(message, "error");
      });
    } else {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur inconnue s'est produite";
      alertStore.addAlert(message, "error");
    }
    return Promise.reject(error);
  }
);

app.config.globalProperties.$moment = moment;
app.config.globalProperties.$formatDateTimeLong = formatDateTimeLong;
app.config.globalProperties.$formatMoney = formatMoney;

app.use(createPinia());
app.use(router);
app.use(
  createAuth0({
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      scope: "openid profile email offline_access"
    },
    cacheLocation: "localstorage",
    useRefreshTokens: true,
  })
);

app.use(gettext);
app.use(VueBaseTooltip)
app.mount("#app");
